import { signal } from "@angular/core";
import { BaseListPageClass } from "./base-list-page-class";
import { SelectedRecipient } from "../models/ui/selected-recipient.model";

// Selection state emitted by an email recipient selector. Two shapes:
//  - pick mode (useFilter=false): explicit selectedIds, accumulated across searches.
//  - select-all mode (useFilter=true): everyone matching the filter, minus excludedIds.
export interface RecipientSelectionState<TFilter> {
    useFilter: boolean;
    filter: TFilter | null;
    selectedIds: number[] | null;
    excludedIds: number[] | null;
    selectedCount: number;
    isValid: boolean;
    selectedItems: SelectedRecipient[];
}

// Shared selection behavior for the email recipient selectors.
//  - Pick mode: a persistent map of hand-picked recipients that accumulates across multiple searches/filters.
//  - Select-all mode: "everyone matching the current filter" (resolved server-side, so it is NOT limited to the
//    loaded page); the same map then tracks individually EXCLUDED recipients.
export abstract class RecipientSelectorBaseClass<TFilter> extends BaseListPageClass {

    // id -> label. When !isSelectAll these are the explicitly selected recipients; when isSelectAll they are
    // the explicitly excluded recipients. Persists across searches so picks accumulate.
    public selectedRecipients = signal<Map<number, string>>(new Map());

    // Server-reported count of recipients matching the current filter; drives the select-all count.
    public totalMatching = signal<number>(0);

    public isSelected(id: number): boolean {
        const tracked = this.selectedRecipients().has(id);
        return this.isSelectAll ? !tracked : tracked;
    }

    public toggleSelection(id: number, label: string, checked: boolean): void {
        // Track the item only when it diverges from the mode default: in pick mode track when checked; in
        // select-all mode track (as an exclusion) when unchecked.
        const shouldTrack = this.isSelectAll ? !checked : checked;
        this.selectedRecipients.update(map => {
            const next = new Map(map);
            if (shouldTrack) {
                next.set(id, label);
            } else {
                next.delete(id);
            }
            return next;
        });
    }

    public deselect(id: number): void {
        // Removing a chip: in pick mode it deselects; in select-all mode the chip is an exclusion, so removing
        // it re-includes the recipient. Either way the id just drops out of the tracked map.
        this.selectedRecipients.update(map => {
            const next = new Map(map);
            next.delete(id);
            return next;
        });
    }

    public override toggleSelectAll(): void {
        this.isSelectAll = !this.isSelectAll;
        this.selectedRecipients.set(new Map());
    }

    public resetSelection(): void {
        this.isSelectAll = false;
        this.selectedRecipients.set(new Map());
        this.totalMatching.set(0);
    }

    public get effectiveSelectedCount(): number {
        return this.isSelectAll
            ? Math.max(0, this.totalMatching() - this.selectedRecipients().size)
            : this.selectedRecipients().size;
    }

    protected buildSelectionState(currentFilter: TFilter): RecipientSelectionState<TFilter> {
        const items = Array.from(this.selectedRecipients(), ([id, label]) => ({ id, label }));

        if (this.isSelectAll) {
            const count = Math.max(0, this.totalMatching() - items.length);
            return {
                useFilter: true,
                filter: currentFilter,
                selectedIds: null,
                excludedIds: items.map(item => item.id),
                selectedCount: count,
                isValid: count > 0,
                selectedItems: items
            };
        }

        return {
            useFilter: false,
            filter: null,
            selectedIds: items.map(item => item.id),
            excludedIds: null,
            selectedCount: items.length,
            isValid: items.length > 0,
            selectedItems: items
        };
    }
}
